import { Link, useParams, Navigate } from "react-router-dom";
import { usePortfolio, usePortfolioClient, BACKEND_URL } from "../hooks/usePortfolio";
import { Reveal } from "../components/Reveal";
import { ThemeDark } from "../components/ThemeDark";

const workPath = (slug) => `/work/${encodeURIComponent(slug)}`;

export default function CaseStudy() {
  const { slug } = useParams();
  const { data: client, isError, isLoading } = usePortfolioClient(slug);
  const { data: clients = [] } = usePortfolio();

  if (isError) return <Navigate to="/work" replace />;
  if (isLoading || !client) return <main className="page-top section" />;

  const idx = clients.findIndex((c) => c.slug === slug);
  const next =
    clients.length > 1 && idx !== -1 ? clients[(idx + 1) % clients.length] : null;
  const isLogoHero = client.hero === client.logo;
  const hasSections = client.sections.length > 0;

  return (
    <main className="page-top case-page" data-testid="case-study-page">
      <section className="section case-intro">
        <Link to="/work" className="text-link back-link" data-testid="back-to-work-link">
          ← Back to Work
        </Link>

        <div className="case-overview">
          <div className="case-overview-copy">
            <p className="eyebrow">Portfolio</p>
            <h1 className="page-headline" data-testid="case-client-name">
              {client.name}
            </h1>

            <div className="case-summary">
              <div className="case-meta">
                {client.industry && (
                  <span className="industry-tag" data-testid="case-industry-tag">{client.industry}</span>
                )}
                <span className="industry-tag">{hasSections ? "Case Study" : "Identity"}</span>
              </div>
              <p className="case-description">
                {client.description || "Selected identity work by Cactus Branding."}
              </p>
            </div>
          </div>

          <Reveal>
            <div
              className={`case-hero ${isLogoHero ? "is-contain" : ""}`}
              style={client.bg ? { background: client.bg } : undefined}
            >
              <img
                src={`${BACKEND_URL}${client.hero}`}
                alt={`${client.name} — hero`}
                data-testid="case-hero-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {client.sections.map((s, i) =>
        s.title || s.text ? (
          <Reveal key={i}>
            <section className={`section case-section ${i % 2 === 1 ? "is-reversed" : ""}`}>
              <figure className="case-section-image">
                {s.image ? (
                  <img
                    src={`${BACKEND_URL}${s.image}`}
                    alt={`${client.name} — ${s.title || i + 1}`}
                    loading="lazy"
                  />
                ) : (
                  <div className="case-missing-image">
                    <span>{s.imageName ? `Add image: ${s.imageName}` : "Add section image"}</span>
                  </div>
                )}
              </figure>
              <div className="case-section-text">
                <p className="eyebrow">{String(i + 1).padStart(2, "0")}</p>
                {s.title && <h2>{s.title}</h2>}
                {s.text && <p>{s.text}</p>}
              </div>
            </section>
          </Reveal>
        ) : (
          <Reveal key={i}>
            <figure className="section case-image-full">
              <img
                src={`${BACKEND_URL}${s.image}`}
                alt={`${client.name} — ${i + 1}`}
                loading="lazy"
              />
            </figure>
          </Reveal>
        )
      )}

      {hasSections && (
        <section className="section case-howto">
          <div>
            <p className="eyebrow">Folder Driven</p>
            <h2>Add more sections without code</h2>
          </div>
          <div>
            <p>
              Add more numbered images inside this project&apos;s <code>work/</code> folder,
              or add a <code>sections</code> array in <code>info.json</code> when you want
              custom titles and descriptions.
            </p>
          </div>
        </section>
      )}

      {next && (
        <ThemeDark>
          <section className="section next-project">
            <p className="eyebrow">Up Next</p>
            <Link
              to={workPath(next.slug)}
              className="next-project-link"
              data-testid="next-project-link"
            >
              {next.name} <span className="next-arrow">→</span>
            </Link>
          </section>
        </ThemeDark>
      )}
    </main>
  );
}
